import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FieldShell } from './field-shell'
import { fieldTokens } from './field-tokens'
import { SubtextState } from './subtext'

interface SelectOption {
	value: string
	label: string
	description?: string
	icon?: React.ReactNode
}

interface SelectFieldProps {
	label?: string
	labelClassName?: string
	value: string | null
	onChange: (value: string | null) => void
	options: SelectOption[]
	placeholder?: string
	disabled?: boolean
	leftIcon?: React.ReactNode
	className?: string
	subtextStrategy?: (state: SubtextState) => { text?: React.ReactNode; visibility: 'always' | 'hover' | 'focus' | 'hoverOrFocus' | 'never' }
	searchPlaceholder?: string
	emptyMessage?: string
}

export function SelectField({
	label,
	labelClassName,
	value,
	onChange,
	options,
	placeholder = "Select option",
	disabled = false,
	leftIcon,
	className,
	subtextStrategy,
	searchPlaceholder = "Search options...",
	emptyMessage = "No options found.",
}: SelectFieldProps) {
	const [open, setOpen] = useState(false)

	const selectedOption = options.find((option) => option.value === value)

	const triggerContent = (
		<div
			className={cn(
				fieldTokens.combobox,
				"flex items-center justify-between",
				!selectedOption && "text-muted-foreground",
				disabled ? "cursor-not-allowed" : "cursor-pointer",
				className
			)}
			role="combobox"
			aria-expanded={open}
		>
			<div className="flex items-center gap-2 flex-1 min-w-0">
				{leftIcon}
				<span className="text-sm truncate">
					{selectedOption ? selectedOption.label : placeholder}
				</span>
			</div>
			<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
		</div>
	)

	if (disabled) {
		return (
			<FieldShell
				label={label}
				labelClassName={labelClassName}
				subtextStrategy={subtextStrategy}
				disabled={disabled}
			>
				{triggerContent}
			</FieldShell>
		)
	}

	return (
		<FieldShell
			label={label}
			labelClassName={labelClassName}
			subtextStrategy={subtextStrategy}
			disabled={disabled}
		>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<div>{triggerContent}</div>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0" align="start">
					<Command>
						<CommandInput placeholder={searchPlaceholder} />
						<CommandList>
							<CommandEmpty>{emptyMessage}</CommandEmpty>
							<CommandGroup>
								{options.map((option) => (
									<CommandItem
										key={option.value}
										value={option.value}
										onSelect={(currentValue) => {
											onChange(currentValue === value ? null : currentValue)
											setOpen(false)
										}}
									>
										<div className="flex items-center gap-3 flex-1">
											{option.icon}
											<div className="flex-1">
												<div className="font-medium">{option.label}</div>
												{option.description && (
													<div className="text-sm text-muted-foreground">
														{option.description}
													</div>
												)}
											</div>
										</div>
										<Check
											className={cn(
												"ml-2 h-4 w-4",
												value === option.value ? "opacity-100" : "opacity-0"
											)}
										/>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</FieldShell>
	)
}
